import {
  useApiUrl,
  useCustom,
  useCustomMutation,
  useNotification,
} from "@refinedev/core";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import {
  type CostSubscriptionFormType,
  toCostSubscriptionPayload,
} from "@/pages/tina/cost-subscriptions/schemas/cost-subscription.form";
import type { CostSubscriptionRead } from "@/types/tina";

const PROVIDER = "tina";
const PATH = "cost-subscriptions";

/**
 * The flat monthly plans, plus create / update / end. `GET /cost-subscriptions`
 * is a plain array (not a page), so it is read through `useCustom` rather than
 * the table hooks, and there is no per-id GET: the edit screen looks its row up
 * in this list.
 */
export const useCostSubscriptions = () => {
  const apiUrl = useApiUrl(PROVIDER);
  const { open } = useNotification();
  const { mutateAsync } = useCustomMutation();

  const query = useCustom<CostSubscriptionRead[]>({
    url: apiUrl,
    method: "get",
    dataProviderName: PROVIDER,
    meta: { urlPath: PATH },
  });

  const send = (method: "post" | "patch", urlPath: string, values: object) =>
    mutateAsync({
      url: apiUrl,
      method,
      dataProviderName: PROVIDER,
      values,
      meta: { urlPath },
      // `run` toasts the failure itself; Refine's default would add a second.
      errorNotification: false,
    });

  const refetch = () => query.query.refetch();

  /** Runs a write, toasts the outcome, and resolves whether it succeeded. */
  const run = async (
    call: () => Promise<unknown>,
    success: string,
    failure: string,
  ): Promise<boolean> => {
    try {
      await call();
      open?.({ type: "success", message: success });
      await refetch();
      return true;
    } catch (error) {
      open?.({
        type: "error",
        message: failure,
        description: toHumanErrorMessage(error),
      });
      return false;
    }
  };

  const create = (values: CostSubscriptionFormType) =>
    run(
      () => send("post", PATH, toCostSubscriptionPayload(values)),
      "Subscription added",
      "Could not add the subscription",
    );

  const update = (id: string, values: CostSubscriptionFormType) =>
    run(
      () => send("patch", `${PATH}/${id}`, toCostSubscriptionPayload(values)),
      "Subscription updated",
      "Could not update the subscription",
    );

  /** Ends a plan: PATCH `ends_on` to `date` (a local `YYYY-MM-DD`). */
  const endPlan = (id: string, date: string) =>
    run(
      () => send("patch", `${PATH}/${id}`, { ends_on: date }),
      "Plan ended",
      "Could not end the plan",
    );

  return {
    // Refine's useCustom yields `{}` (not undefined) while loading or after an
    // error, so `?? []` alone would hand the page an object.
    subscriptions: Array.isArray(query.result?.data) ? query.result.data : [],
    isLoading: query.query.isLoading,
    isError: query.query.isError,
    refetch,
    create,
    update,
    endPlan,
  };
};
